import { ActionTreeNode } from '../models/actions-tree-node.model'
import { TreeTableData, TreeTableSection, TreeTableEntityNode } from '../models/actions-tree-node.model'

export class JMeleonTreeTableTransformUtils {
  // Standard actions that get columns
  private static readonly STANDARD_ACTIONS = ['create', 'read', 'write', 'delete', 'export', 'upload', 'download']

  /**
   * Creates an object with standard actions initialized to undefined.
   *
   * @returns An object with standard actions as keys and undefined values
   * @internal
   */
  private static createEmptyActionsObject(): { [key: string]: boolean | undefined } {
    return this.STANDARD_ACTIONS.reduce(
      (acc, action) => {
        acc[action] = undefined
        return acc
      },
      {} as { [key: string]: boolean | undefined }
    )
  }

  /**
   * Transforms an ActionTreeNode into a TreeTableData structure.
   *
   * @param rootNode The root node containing sections to be transformed
   * @returns A TreeTableData object with transformed sections and total selected actions
   * @internal
   */
  static transformToTreeTable(rootNode: ActionTreeNode): TreeTableData {
    if (!rootNode?.nodes) {
      return { sections: [], totalSelectedActions: 0 }
    }

    const sections: TreeTableSection[] = []
    let totalSelected = 0

    // Process each section (first level nodes)
    rootNode.nodes.forEach((sectionNode) => {
      const section = this.transformSection(sectionNode)
      sections.push(section)
      totalSelected += section.selectedCount || 0
    })

    return {
      sections,
      totalSelectedActions: totalSelected
    }
  }

  private static transformSection(sectionNode: ActionTreeNode): TreeTableSection {
    const entities: TreeTableEntityNode[] = []
    let selectedCount = 0

    if (sectionNode.nodes) {
      sectionNode.nodes.forEach((entityNode) => {
        // ✅ Check if this is a root-level dynamic action
        if ((!entityNode.nodes || entityNode.nodes.length === 0) && !this.STANDARD_ACTIONS.includes(entityNode.name)) {
          // This is a root-level dynamic action like "autoLoginActiveDevices"
          const actions = this.createEmptyActionsObject()
          actions[entityNode.name] = entityNode.activated || false

          const entity: TreeTableEntityNode = {
            name: entityNode.name,
            module: entityNode.module,
            level: 0,
            actions,
            children: undefined,
            expanded: false,
            selected: entityNode.activated || false,
            description: entityNode.description,
            hasNonStandardActions: true,
            parent: undefined
          }

          entities.push(entity)
          selectedCount += this.countEntityActions(entity.actions)
        } else {
          // Regular entity processing
          const isolatedEntity = this.transformEntityWithHierarchy(entityNode, 0)
          entities.push(isolatedEntity)
          selectedCount += this.countSelectedActions(isolatedEntity)
        }
      })
    }

    return {
      name: sectionNode.name,
      module: sectionNode.module,
      entities,
      hasSelectedActions: selectedCount > 0,
      selectedCount
    }
  }

  private static processEntityNodes(
    entityNode: ActionTreeNode,
    level: number,
    processChildEntity: (childNode: ActionTreeNode, level: number) => TreeTableEntityNode
  ): {
    actions: { [key: string]: boolean | undefined }
    children: TreeTableEntityNode[]
    hasNonStandardActions: boolean
  } {
    const actions = this.createEmptyActionsObject()
    const children: TreeTableEntityNode[] = []
    let hasNonStandardActions = false

    if (entityNode.nodes) {
      entityNode.nodes.forEach((childNode) => {
        if (this.STANDARD_ACTIONS.includes(childNode.name)) {
          // ✅ Standard action → goes to actions
          actions[childNode.name] = childNode.activated || false
        } else if (!childNode.nodes || childNode.nodes.length === 0) {
          // ✅ Leaf node = Dynamic Action → goes to actions (not children!)
          actions[childNode.name] = childNode.activated || false
          hasNonStandardActions = true
        } else {
          // ✅ Has children = Real Entity → goes to children
          const childEntity = processChildEntity(childNode, level + 1)
          children.push(childEntity)
        }
      })
    }

    return { actions, children, hasNonStandardActions }
  }

  private static transformEntityWithHierarchy(
    entityNode: ActionTreeNode,
    level: number,
    parent?: TreeTableEntityNode
  ): TreeTableEntityNode {
    const { actions, children, hasNonStandardActions } = this.processEntityNodes(
      entityNode,
      level,
      // ✅ Pass parent reference to children
      (childNode, childLevel) => this.transformEntityWithHierarchy(childNode, childLevel, undefined) // Will be set below
    )

    const entity: TreeTableEntityNode = {
      name: entityNode.name,
      module: entityNode.module,
      level,
      actions,
      children: children.length > 0 ? children : undefined,
      expanded: false,
      selected: entityNode.activated || false,
      description: entityNode.description,
      hasNonStandardActions,
      parent // ✅ Set parent reference
    }

    // ✅ Set parent reference for all children
    if (entity.children) {
      entity.children.forEach((child) => {
        child.parent = entity
      })
    }

    return entity
  }

  /**
   * Transforms an entity node into a TreeTableEntityNode with standard processing.
   *
   * This method recursively processes entity nodes, handling standard actions,
   * and maintaining the hierarchical structure of the tree.
   *
   * @param entityNode The root entity node to transform
   * @param level The current hierarchy level of the entity
   * @returns A transformed TreeTableEntityNode representing the entity and its children
   * @internal
   */
  private static transformEntity(entityNode: ActionTreeNode, level: number): TreeTableEntityNode {
    const { actions, children, hasNonStandardActions } = this.processEntityNodes(
      entityNode,
      level,
      // Pass the standard processor
      (childNode, childLevel) => this.transformEntity(childNode, childLevel)
    )

    return {
      name: entityNode.name,
      module: entityNode.module,
      level,
      actions,
      children: children.length > 0 ? children : undefined,
      expanded: false,
      selected: entityNode.activated || false,
      description: entityNode.description,
      hasNonStandardActions
    }
  }

  /**
   * Recursively counts the total number of selected actions across an entire tree table entity hierarchy.
   *
   * This method performs a depth-first traversal of the tree, counting selected actions while preventing
   * infinite loops through cycle detection using a unique entity identifier.
   *
   * @param rootEntity The top-level entity node to start counting selected actions from
   * @returns The total number of selected actions in the entity hierarchy
   * @internal
   */
  private static countSelectedActions(rootEntity: TreeTableEntityNode): number {
    let totalCount = 0
    const visited = new Set<string>()
    const stack: TreeTableEntityNode[] = [rootEntity]

    while (stack.length > 0) {
      const currentEntity = stack.pop()!

      // Create unique identifier for cycle detection
      const entityId = `${currentEntity.module || 'unknown'}-${currentEntity.name}-${currentEntity.level}`

      // Skip if already processed (cycle detection)
      if (visited.has(entityId)) {
        continue
      }
      visited.add(entityId)

      // Count selected actions in current entity
      totalCount += this.countEntityActions(currentEntity.actions)

      // Add children to stack for processing (if any)
      if (currentEntity.children?.length) {
        stack.push(...currentEntity.children)
      }
    }

    return totalCount
  }

  /**
   * Counts the number of selected actions within a given actions object.
   *
   * @param actions An object mapping action names to their selection status
   * @returns The total number of actions that are explicitly set to true
   * @internal
   */
  private static countEntityActions(actions: { [key: string]: boolean | undefined }): number {
    return Object.values(actions).filter((selected) => selected === true).length
  }
}
